import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { erpRouteElements } from './routes/ErpRoutes'
import LoadingSpinner from './components/LoadingSpinner'
import ProtectedRoute from './components/ProtectedRoute'
import RoleRoute from './components/RoleRoute'
import HelpLayout from './pages/common/Help/HelpLayout'
import { useAuth } from './contexts/AuthContext'

const RootRedirect = () => {
  const { user } = useAuth()
  return <Navigate to={user?.landing_route || '/tickets/hub'} replace />
}

const HelpHome = lazy(() => import('./pages/common/Help/HelpHome'))
const HelpArticle = lazy(() => import('./pages/common/Help/HelpArticle'))
const HelpSearch = lazy(() => import('./pages/common/Help/HelpSearch'))

const OperationsHubLanding = lazy(() => import('./pages/common/Tickets/OperationsHubLanding'))
const TicketingLayout = lazy(() => import('./pages/common/Tickets/TicketingLayout'))
const TicketList = lazy(() => import('./pages/common/Tickets/TicketList'))
const TicketSubmit = lazy(() => import('./pages/common/Tickets/TicketSubmit'))
const TicketDetail = lazy(() => import('./pages/common/Tickets/TicketDetail'))
const BridgePage = lazy(() => import('./pages/common/Tickets/BridgePage'))
const SupportDashboard = lazy(() => import('./pages/common/Tickets/SupportDashboard'))
const TicketAuditActivity = lazy(() => import('./pages/common/Tickets/TicketAuditActivity'))
const ConfigurationItems = lazy(() => import('./pages/common/Tickets/ConfigurationItems'))
const GroupManagement = lazy(() => import('./pages/common/Tickets/GroupManagement'))
const KnowledgeCreate = lazy(() => import('./pages/common/Tickets/KnowledgeCreate'))
const KnowledgeList = lazy(() => import('./pages/common/Tickets/KnowledgeList'))
const KnowledgeBaseCreate = lazy(() => import('./pages/common/Tickets/KnowledgeBaseCreate'))
const KnowledgeBaseList = lazy(() => import('./pages/common/Tickets/KnowledgeBaseList'))
const AppDashboard = lazy(() => import('./pages/common/Tickets/AppDashboard'))
const ConfigDashboard = lazy(() => import('./pages/common/Tickets/ConfigDashboard'))
const GroupDashboard = lazy(() => import('./pages/common/Tickets/GroupDashboard'))
const WorkforceOperationsDashboard = lazy(() => import('./pages/common/Tickets/WorkforceOperationsDashboard'))
const UserDashboard = lazy(() => import('./pages/common/Tickets/UserDashboard'))
const ChangeDashboard = lazy(() => import('./pages/common/Tickets/ChangeDashboard'))
const ClientDashboard = lazy(() => import('./pages/common/Tickets/ClientDashboard'))
const CmdbTopologyDashboard = lazy(() => import('./pages/common/Tickets/CmdbTopologyDashboard'))
const AssetsCmdbMindMap = lazy(() => import('./pages/common/Tickets/AssetsCmdbMindMap'))
const DashboardPermissions = lazy(() => import('./pages/common/Tickets/DashboardPermissions'))
const MenuAccessConfig = lazy(() => import('./pages/common/Tickets/MenuAccessConfig'))
const SlaConfig = lazy(() => import('./pages/common/Tickets/SlaConfig'))
const EscalationMatrixConfig = lazy(() => import('./pages/common/Tickets/EscalationMatrixConfig'))
const ReferenceDataConfig = lazy(() => import('./pages/common/Tickets/ReferenceDataConfig'))
const SupportEmailConfig = lazy(() => import('./pages/common/Tickets/SupportEmailConfig'))
const MonitoringSources = lazy(() => import('./pages/common/Tickets/MonitoringSources'))
const MajorIncidentWorkbench = lazy(() => import('./pages/common/Tickets/MajorIncidentWorkbench'))
const IncidentReports = lazy(() => import('./pages/common/Tickets/IncidentReports'))
const IncidentCommandDashboard = lazy(() => import('./pages/common/Tickets/IncidentCommandDashboard'))
const NocTelemetryDashboard = lazy(() => import('./pages/common/Tickets/NocTelemetryDashboard'))
const AutonomousResponseDashboard = lazy(() => import('./pages/common/Tickets/AutonomousResponseDashboard'))
const VoiceAssistantHub = lazy(() => import('./pages/common/Tickets/VoiceAssistantHub'))
const ServiceCatalog = lazy(() => import('./pages/common/Tickets/ServiceCatalog'))
const ProblemManagementWorkbench = lazy(() => import('./pages/common/Tickets/ProblemManagementWorkbench'))
const ChangeManagementWorkbench = lazy(() => import('./pages/common/Tickets/ChangeManagementWorkbench'))
const ClientOnboard = lazy(() => import('./pages/common/Tickets/ClientOnboard'))
const ItsmBenchmarksDashboard = lazy(() => import('./pages/common/Tickets/ItsmBenchmarksDashboard'))
const ReleaseManagement = lazy(() => import('./pages/common/Tickets/ReleaseManagement'))
const ItsmAdvancedHub = lazy(() => import('./pages/common/Tickets/ItsmAdvancedHub'))
const PredictiveIntelligence = lazy(() => import('./pages/common/Tickets/PredictiveIntelligence'))
const VirtualAgentConsole = lazy(() => import('./pages/common/Tickets/VirtualAgentConsole'))
const OnCallScheduling = lazy(() => import('./pages/common/Tickets/OnCallScheduling'))
const WalkUpExperience = lazy(() => import('./pages/common/Tickets/WalkUpExperience'))
const WorkforceOptimization = lazy(() => import('./pages/common/Tickets/WorkforceOptimization'))
const AssetManagement = lazy(() => import('./pages/common/Tickets/AssetManagement'))
const ContinualImprovement = lazy(() => import('./pages/common/Tickets/ContinualImprovement'))
const DevOpsVelocity = lazy(() => import('./pages/common/Tickets/DevOpsVelocity'))
const DigitalExperience = lazy(() => import('./pages/common/Tickets/DigitalExperience'))
const AgentCoaching = lazy(() => import('./pages/common/Tickets/AgentCoaching'))

const LoginPage = lazy(() => import('./pages/common/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/common/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/common/ResetPasswordPage'))
const NotFoundPage = lazy(() => import('./pages/common/NotFoundPage'))

const AdminRoute = ({ children }) => (
  <RoleRoute requireAdmin>{children}</RoleRoute>
)

function App() {
  return (
    <>
      <Suspense fallback={<LoadingSpinner />}><Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        <Route path="/help" element={
          <ProtectedRoute>
            <HelpLayout />
          </ProtectedRoute>
        }>
          <Route index element={<HelpHome />} />
          <Route path="search" element={<HelpSearch />} />
          <Route path=":slug" element={<HelpArticle />} />
        </Route>

        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <TicketingLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="hub" replace />} />
          <Route path="hub" element={<OperationsHubLanding />} />
          <Route path="my" element={<TicketList />} />
          <Route path="dashboard" element={<SupportDashboard />} />
          <Route path="support" element={<SupportDashboard />} />
          <Route path="audit" element={<TicketAuditActivity />} />
          <Route path="configuration-items" element={<ConfigurationItems />} />
          <Route path="group-management" element={<GroupManagement />} />
          <Route path="knowledge" element={<KnowledgeList />} />
          <Route path="knowledge/new" element={<KnowledgeCreate />} />
          <Route path="knowledge/:editId/edit" element={<KnowledgeCreate />} />
          <Route path="knowledge/view/:slug" element={<HelpArticle />} />
          <Route path="knowledge-base" element={<KnowledgeBaseList />} />
          <Route path="knowledge-base/new" element={<KnowledgeBaseCreate />} />
          <Route path="knowledge-base/:kbId" element={<KnowledgeBaseCreate />} />
          <Route path="app-dashboard" element={<AppDashboard />} />
          <Route path="config-dashboard" element={<CmdbTopologyDashboard />} />
          <Route path="cmdb-topology" element={<CmdbTopologyDashboard />} />
          <Route path="assets-cmdb-investigate" element={<AssetsCmdbMindMap />} />
          <Route path="cmdb-mindmap" element={<AssetsCmdbMindMap />} />
          <Route path="group-dashboard" element={<WorkforceOperationsDashboard />} />
          <Route path="workforce-operations" element={<WorkforceOperationsDashboard />} />
          <Route path="user-dashboard" element={<UserDashboard />} />
          <Route path="change-dashboard" element={<ChangeDashboard />} />
          <Route path="client-dashboard" element={<ClientDashboard />} />
          <Route path="client-operations" element={<ClientDashboard />} />
          <Route path="dashboard-permissions" element={<AdminRoute><DashboardPermissions /></AdminRoute>} />
          <Route path="menu-access" element={<AdminRoute><MenuAccessConfig /></AdminRoute>} />
          <Route path="sla-config" element={<AdminRoute><SlaConfig /></AdminRoute>} />
          <Route path="escalation-matrix" element={<AdminRoute><EscalationMatrixConfig /></AdminRoute>} />
          <Route path="reference-data" element={<AdminRoute><ReferenceDataConfig /></AdminRoute>} />
          <Route path="support-email-config" element={<AdminRoute><SupportEmailConfig /></AdminRoute>} />
          <Route path="monitoring-sources" element={<MonitoringSources />} />
          <Route path="major-incidents" element={<MajorIncidentWorkbench />} />
          <Route path="incident-reports" element={<IncidentReports />} />
          <Route path="incident-command" element={<IncidentCommandDashboard />} />
          <Route path="noc-telemetry" element={<NocTelemetryDashboard />} />
          <Route path="autonomous-response" element={<AutonomousResponseDashboard />} />
          <Route path="voice-assistant" element={<VoiceAssistantHub />} />
          <Route path="service-catalog" element={<ServiceCatalog />} />
          <Route path="problem-management" element={<ProblemManagementWorkbench />} />
          <Route path="change-management" element={<ChangeManagementWorkbench />} />
          <Route path="client-onboard" element={<AdminRoute><ClientOnboard /></AdminRoute>} />
          <Route path="itsm-benchmarks" element={<ItsmBenchmarksDashboard />} />
          <Route path="releases" element={<ReleaseManagement />} />
          <Route path="itsm-advanced" element={<ItsmAdvancedHub />} />
          <Route path="predictive-intelligence" element={<PredictiveIntelligence />} />
          <Route path="virtual-agent" element={<VirtualAgentConsole />} />
          <Route path="on-call" element={<OnCallScheduling />} />
          <Route path="walk-up" element={<WalkUpExperience />} />
          <Route path="workforce-optimization" element={<WorkforceOptimization />} />
          <Route path="asset-management" element={<AssetManagement />} />
          <Route path="continual-improvement" element={<ContinualImprovement />} />
          <Route path="devops-velocity" element={<DevOpsVelocity />} />
          <Route path="digital-experience" element={<DigitalExperience />} />
          <Route path="agent-coaching" element={<AgentCoaching />} />
          <Route path="new" element={<TicketSubmit />} />
          <Route path=":id" element={<TicketDetail />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="/tickets/:id/bridge/:bridgeType" element={
          <ProtectedRoute>
            <BridgePage />
          </ProtectedRoute>
        } />

        {erpRouteElements}

        <Route path="/" element={
          <ProtectedRoute>
            <RootRedirect />
          </ProtectedRoute>
        } />
        <Route path="*" element={<NotFoundPage />} />
      </Routes></Suspense>
    </>
  )
}

export default App
